import { INTENTOS_KEY, MAX_INTENTOS } from './chunkErrorRecovery'

/**
 * Recuperación de la carga inicial cuando un chunk del arranque falla.
 *
 * `ChunkErrorRecovery` cubre los fallos con la app ya andando, pero vive dentro de React: si el que
 * falla es uno de los `<script>` iniciales que arrancan React, el componente nunca se monta y la
 * pantalla se queda cargando. Pasó en UAT el 4-sep-2026 a las 15:36:38, justo al terminar el
 * despliegue de las 15:36: la pestaña abierta pidió un chunk de la versión anterior, recibió 500 en
 * `text/plain` y el navegador se negó a ejecutarlo («Refused to execute script … MIME type
 * ('text/plain')»).
 *
 * Este script va en línea en el `<head>` del layout raíz, antes de cualquier chunk, y comparte el
 * presupuesto de recargas con el helper (misma clave de sessionStorage, mismo máximo) para que entre
 * los dos no recarguen en bucle. Cuando React monta `ChunkErrorRecovery`, este oyente se retira.
 */

/** Nombre del objeto global que el script deja para que React le ceda el control. */
export const GLOBAL_RECUPERACION_INICIAL = '__xtsRecuperacionInicial'

/** Respiro antes de recargar: el despliegue que dejó el hueco puede estar terminando. */
export const MS_ANTES_DE_RECARGAR = 1500

const AVISO_AGOTADO =
  'El sistema se está actualizando. Espere unos segundos y vuelva a cargar la página.'

/** Script en línea, sin dependencias, listo para `dangerouslySetInnerHTML`. */
export const scriptRecuperacionInicial = (): string => `(function () {
  var KEY = ${JSON.stringify(INTENTOS_KEY)};
  var MAX = ${MAX_INTENTOS};
  var ESPERA = ${MS_ANTES_DE_RECARGAR};
  var disparado = false;
  function intentos() {
    try { return Number(window.sessionStorage.getItem(KEY) || 0) || 0; } catch (e) { return 0; }
  }
  function recargar() {
    if (window.__xtsRecargar) { window.__xtsRecargar(); } else { window.location.reload(); }
  }
  function avisar() {
    var aviso = document.createElement('div');
    aviso.setAttribute('role', 'alert');
    aviso.style.cssText = 'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#fff;font:15px system-ui,sans-serif;color:#333;padding:24px;text-align:center;z-index:2147483647';
    aviso.textContent = ${JSON.stringify(AVISO_AGOTADO)};
    (document.body || document.documentElement).appendChild(aviso);
  }
  function onError(ev) {
    var el = ev.target;
    if (!el || el === window) { return; }
    var url = el.tagName === 'SCRIPT' ? el.src : (el.tagName === 'LINK' ? el.href : '');
    if (!url || url.indexOf('/_next/static/') === -1 || disparado) { return; }
    disparado = true;
    var n = intentos();
    if (n >= MAX) { avisar(); return; }
    try { window.sessionStorage.setItem(KEY, String(n + 1)); } catch (e) {}
    window.setTimeout(recargar, ESPERA);
  }
  window.addEventListener('error', onError, true);
  window.${GLOBAL_RECUPERACION_INICIAL} = {
    desactivar: function () { window.removeEventListener('error', onError, true); }
  };
})();`

interface IWindowConRecuperacion extends Window {
  [GLOBAL_RECUPERACION_INICIAL]?: { desactivar: () => void }
}

/** La llama `ChunkErrorRecovery` al montarse: React ya arrancó y toma el relevo. */
export const desactivarRecuperacionInicial = (): void => {
  if (typeof window === 'undefined') {
    return
  }
  ;(window as IWindowConRecuperacion)[GLOBAL_RECUPERACION_INICIAL]?.desactivar()
}
